import React from "react";
import { useBreadcrumbs } from "contexts/BreadcrumbsContext";
import FuelSidebar from "./components/FuelSidebar";
import FuelAnalytics from "./components/FuelAnalytics";
import {
    fetchFuelSidebar,
    fetchFuelCityAnalytics,
    type FuelSidebarRow,
    type FuelCityAnalyticsResponse,
} from "./api";

const FuelPricesPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();

    const [sidebarRows, setSidebarRows] = React.useState<FuelSidebarRow[]>([]);
    const [selectedCity, setSelectedCity] = React.useState<string>("");

    const [analytics, setAnalytics] = React.useState<FuelCityAnalyticsResponse | null>(null);

    const [sidebarLoading, setSidebarLoading] = React.useState<boolean>(true);
    const [analyticsLoading, setAnalyticsLoading] = React.useState<boolean>(true);

    const [sidebarError, setSidebarError] = React.useState<string>("");
    const [analyticsError, setAnalyticsError] = React.useState<string>("");

    const [search, setSearch] = React.useState("");
    const [debouncedSearch, setDebouncedSearch] = React.useState("");
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(20);
    const [totalItems, setTotalItems] = React.useState(0);

    const [dateRange, setDateRange] = React.useState<"1W" | "1M" | "3M" | "CUSTOM">("1W");
    const [fromDate, setFromDate] = React.useState("");
    const [toDate, setToDate] = React.useState("");

    const [sortBy, setSortBy] = React.useState<"city" | "price">("city");
    const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");

    React.useEffect(() => {
        setBreadcrumbs([
            { label: "Reports", href: "/reports" },
            { label: "Diesel Prices" },
        ]);
    }, [setBreadcrumbs]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 400);

        return () => clearTimeout(timer);
    }, [search]);

    const buildDateRange = React.useCallback(
        (range: "1W" | "1M" | "3M" | "CUSTOM") => {
            const today = new Date();

            const to = today.toISOString().split("T")[0];
            const from = new Date(today);

            if (range === "1W") {
                from.setDate(today.getDate() - 6);
            }

            if (range === "1M") {
                from.setMonth(today.getMonth() - 1);
            }

            if (range === "3M") {
                from.setMonth(today.getMonth() - 3);
            }

            return {
                from_date: from.toISOString().split("T")[0],
                to_date: to,
            };
        },
        []
    );

    React.useEffect(() => {
        if (dateRange === "CUSTOM") {
            return;
        }

        const dates = buildDateRange(dateRange);

        setFromDate(dates.from_date);
        setToDate(dates.to_date);
    }, [dateRange, buildDateRange]);

    React.useEffect(() => {
        const loadSidebar = async () => {
            try {
                setSidebarLoading(true);
                setSidebarError("");

                const resp = await fetchFuelSidebar({
                    city: debouncedSearch || undefined,
                    page,
                    per_page: pageSize,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                });

                setSidebarRows(resp.rows || []);
                setTotalItems(resp.total_items || 0);

                const firstNonIndex = resp.rows.find((r) => r.City.toLowerCase() !== "index");
                if (firstNonIndex) {
                    setSelectedCity(firstNonIndex.City);
                }
            } catch (err: any) {
                setSidebarError(
                    err?.response?.data?.detail ||
                        "Failed to load sidebar"
                );
            } finally {
                setSidebarLoading(false);
            }
        };
        loadSidebar();
    }, [debouncedSearch, page, pageSize, sortBy, sortOrder]);

    React.useEffect(() => {
        if (!selectedCity || !fromDate || !toDate) {
            return;
        }

        const loadAnalytics = async () => {
            try {
                setAnalyticsLoading(true);
                setAnalyticsError("");

                const resp = await fetchFuelCityAnalytics({
                    city: selectedCity,
                    from_date: fromDate,
                    to_date: toDate || "2026-05-26",
                });

                setAnalytics(resp);

                // Backend returned valid response
                // but no analytics available
                if (!resp.summary) {
                    setAnalyticsError(resp.message || "No analytics available");
                } else {
                    setAnalyticsError("");
                }
            } catch (err: any) {
                setAnalytics(null);
                setAnalyticsError(
                    err?.response?.data?.detail || "Failed to load analytics"
                );
            } finally {
                setAnalyticsLoading(false);
            }
        };
        loadAnalytics();
    }, [selectedCity, fromDate, toDate]);

    return (
        <div className="h-[calc(100vh-64px)] overflow-hidden p-4">
            <div className="h-full flex gap-4 overflow-hidden">
                <div className="w-2/5 min-w-[320px] max-w-[480px] h-full overflow-hidden">
                    <FuelSidebar
                        rows={sidebarRows}
                        loading={sidebarLoading}
                        error={sidebarError}
                        selectedCity={selectedCity}
                        onCitySelect={setSelectedCity}
                        search={search}
                        onSearchChange={setSearch}
                        page={page}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSortChange={(by, order) => {
                            setPage(1);
                            setSortBy(by);
                            setSortOrder(order);
                        }}
                    />
                </div>

                <div className="flex-1 min-w-0 h-full overflow-hidden">
                    <FuelAnalytics
                        data={analytics}
                        loading={analyticsLoading}
                        error={analyticsError}
                        dateRange={dateRange}
                        onDateRangeChange={setDateRange}
                        fromDate={fromDate}
                        toDate={toDate}
                        onFromDateChange={setFromDate}
                        onToDateChange={setToDate}
                    />
                </div>
            </div>
        </div>
    );
};

export default FuelPricesPage;